<script setup lang="ts">
import type { Schemas } from "#shopware";
import {
  getTranslatedProperty,
  getCategoryRoute,
} from "@shopware/helpers";

type NavigationElement = Schemas["Category"] & {
  externalLink?: string;
  linkNewTab?: boolean;
};

const navigationElements = inject<Ref<NavigationElement[]>>("swNavigation-footer-navigation");
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const gridColumns = computed<number>(() =>
  navigationElements?.value
    ? Object.keys(navigationElements.value).length + 2
    : 2,
);

// Helper function to get the correct link for navigation items
const getNavigationLink = (navigationChild: NavigationElement): any => {
  // If external link exists, use it directly without formatLink
  if (navigationChild.externalLink) {
    return navigationChild.externalLink;
  }
  // Otherwise, use the normal route handling
  const route = getCategoryRoute(navigationChild);
  // @ts-expect-error - formatLink can return string or RouteObject, both are valid for NuxtLink
  return formatLink({ path: route });
};
</script>

<template>
  <LayoutCustomFooter />
  <footer class="white-bg-sec">
    <div class="container">
      <div class="row">
        <div class="col-md-4 col-12">
          <NuxtLink :to="formatLink(`/`)">
            <img loading="lazy" class="img-fluid mb-4 footer-logo" src="assets/images/site-logo.png" alt="">
          </NuxtLink>
          <p class="footer-txt">&nbsp;</p>
          <div class="d-flex mt-4 flex-wrap footer-payments">
            <img loading="lazy" src="assets/images/visa.svg" alt="Visa">
            <img loading="lazy" src="assets/images/mastercard.svg" alt="Mastercard">
            <img loading="lazy" src="assets/images/postfinance.svg" alt="PostFinance">
            <img loading="lazy" src="assets/images/twint.svg" alt="Twint">
          </div>
        </div>
        <div class="col-md-8">
          <div class="row contact-info gy-md-4">
            <div class="col-lg-3 col-md-6">
              <img loading="lazy" src="assets/images/location.svg" alt="">
              <p>Promofashion AG<br>Unterdorfstrasse 66<br>CH-4934 Madiswil </p>
            </div>

            <div class="col-lg-3 col-md-6 col-6">
              <img loading="lazy" src="assets/images/phone.svg" alt="">
              <p>Telefon:<br> <a class="text-black" href="tel:+41629267099">+41 (0) 62 926 70 99</a></p>
            </div>

            <div class="col-lg-3 col-md-6 col-6">
              <img loading="lazy" src="assets/images/email.svg" alt="">
              <p>E-Mail:<br><a class="text-black" href="mailto:info@geschenkdosen.ch">info@geschenkdosen.ch</a></p>
            </div>


            <div class="col-lg-3 col-md-6 align-self-lg-end">
              <div class="social-media">
                <a href="https://www.facebook.com/Geschenkdosenteam/" target="_blank">
                  <svg width="14" height="26" viewBox="0 0 14 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M14 0.307993V4.33383H11.615C10.7441 4.33383 10.1567 4.51683 9.85286 4.88281C9.54904 5.2488 9.39714 5.79778 9.39714 6.52975V9.41188H13.8481L13.2556 13.9257H9.39714V25.5H4.7487V13.9257H0.875V9.41188H4.7487V6.08752C4.7487 4.19659 5.27532 2.73011 6.32856 1.68806C7.3818 0.646021 8.78443 0.125 10.5365 0.125C12.0252 0.125 13.1797 0.185998 14 0.307993Z"
                      fill="#D4AF37" />
                  </svg>
                </a>
                <a href="https://www.instagram.com/geschenkdosen/" target="_blank">
                  <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd"
                      d="M5.78027 0.574219C4.39937 0.574219 3.07499 1.12264 2.09836 2.0989C1.12173 3.07516 0.572796 4.39932 0.572266 5.78022V19.6682C0.572266 21.0495 1.12096 22.3741 2.09765 23.3508C3.07434 24.3275 4.39902 24.8762 5.78027 24.8762H19.6683C21.0492 24.8757 22.3733 24.3268 23.3496 23.3501C24.3258 22.3735 24.8743 21.0491 24.8743 19.6682V5.78022C24.8737 4.39967 24.3251 3.07581 23.3489 2.09961C22.3727 1.12341 21.0488 0.574749 19.6683 0.574219H5.78027ZM21.1663 5.78822C21.1663 6.18605 21.0082 6.56758 20.7269 6.84888C20.4456 7.13019 20.0641 7.28822 19.6663 7.28822C19.2684 7.28822 18.8869 7.13019 18.6056 6.84888C18.3243 6.56758 18.1663 6.18605 18.1663 5.78822C18.1663 5.3904 18.3243 5.00886 18.6056 4.72756C18.8869 4.44625 19.2684 4.28822 19.6663 4.28822C20.0641 4.28822 20.4456 4.44625 20.7269 4.72756C21.0082 5.00886 21.1663 5.3904 21.1663 5.78822ZM12.7263 8.56422C11.623 8.56422 10.5649 9.0025 9.7847 9.78266C9.00455 10.5628 8.56627 11.6209 8.56627 12.7242C8.56627 13.8275 9.00455 14.8856 9.7847 15.6658C10.5649 16.4459 11.623 16.8842 12.7263 16.8842C13.8296 16.8842 14.8877 16.4459 15.6678 15.6658C16.448 14.8856 16.8863 13.8275 16.8863 12.7242C16.8863 11.6209 16.448 10.5628 15.6678 9.78266C14.8877 9.0025 13.8296 8.56422 12.7263 8.56422ZM6.56427 12.7242C6.56427 11.0905 7.21327 9.52367 8.36849 8.36844C9.52371 7.21322 11.0905 6.56422 12.7243 6.56422C14.358 6.56422 15.9248 7.21322 17.08 8.36844C18.2353 9.52367 18.8843 11.0905 18.8843 12.7242C18.8843 14.358 18.2353 15.9248 17.08 17.08C15.9248 18.2352 14.358 18.8842 12.7243 18.8842C11.0905 18.8842 9.52371 18.2352 8.36849 17.08C7.21327 15.9248 6.56427 14.358 6.56427 12.7242Z"
                      fill="#D4AF37" />
                  </svg>

                </a>
              </div>
            </div>
            <div class="mt-md-5 mt-4">
              <template v-for="navigationElement in navigationElements" :key="navigationElement.id">
                <template v-if="navigationElement.childCount > 0">
                  <ul class="d-flex bottom-links w-100 justify-content-lg-between  pe-lg-0 pl-0">
                    <li v-for="navigationChild in navigationElement.children" :key="navigationChild.id">
                      <!-- Use regular anchor tag for external links to preserve trailing slashes -->
                      <a
                        v-if="navigationChild.externalLink"
                        :href="navigationChild.externalLink"
                        :target="navigationChild.linkNewTab ? '_blank' : ''"
                      >
                        {{ getTranslatedProperty(navigationChild, "name") }}
                      </a>
                      <!-- Use NuxtLink for internal links -->
                      <NuxtLink
                        v-else
                        :to="getNavigationLink(navigationChild)"
                        :target="navigationChild.linkNewTab ? '_blank' : ''"
                      >
                        {{ getTranslatedProperty(navigationChild, "name") }}
                      </NuxtLink>
                    </li>
                  </ul>
                </template>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>
